import { Box, Center, Flex } from '@chakra-ui/react';
import { FormattedMessage, useIntl } from 'react-intl';
import { GetIcon } from '../../../components/icons';
import { Button, Header, Text } from '../../../ui-components';

type GiftCardsYouGiveSectionProps = {
    onCheckBalanceClick: () => void;
};

export const GiftCardsYouGiveSection = ({
    onCheckBalanceClick,
}: GiftCardsYouGiveSectionProps): JSX.Element => {
    const intl = useIntl();

    return (
        <Flex
            direction="column"
            gap={8}
            paddingY={{
                base: 4,
                sm: 4,
            }}
            paddingX={{ base: 4, sm: 8, md: 8 }}
            borderRadius={{
                base: 0,
                md: 16,
            }}
            data-testid="gift-cards-you-give-card-section"
            boxShadow="0px 2px 10px 0px rgba(65, 64, 66, 0.14)"
        >
            <Header as="h3" variant="header2" align="center" style="bold" capitalized>
                <FormattedMessage
                    id="gift_cards.landing_page.you_give_card_section.title"
                    defaultMessage="The Gift of Choice"
                />
            </Header>
            <Box
                display="grid"
                gridTemplateColumns={{ base: '1fr', md: 'repeat(4, 1fr)' }}
                gap={8}
                data-testid="gift-cards-you-give-card-section-cards"
            >
                <CardSection
                    icon={<GetIcon name="store" size="lg" />}
                    title={intl.formatMessage({
                        id: 'gift_cards.landing_page.you_give_card_section.redeem_in_store_and_online.title',
                        defaultMessage: 'Redeem In-Store & Online',
                    })}
                    description={intl.formatMessage({
                        id: 'gift_cards.landing_page.you_give_card_section.redeem_in_store_and_online.description',
                        defaultMessage:
                            'Enjoy the freedom of using your gift card wherever you shop—in-store or online!',
                    })}
                />
                <CardSection
                    icon={<GetIcon name="whisk" size="lg" />}
                    title={intl.formatMessage({
                        id: 'gift_cards.landing_page.you_give_card_section.gift_a_cooking_class.title',
                        defaultMessage: 'Gift a Cooking Class',
                    })}
                    description={intl.formatMessage({
                        id: 'gift_cards.landing_page.you_give_card_section.gift_a_cooking_class.description',
                        defaultMessage:
                            'Share the joy of cooking! Gift a memorable culinary experience with a class tailored to every skill level.',
                    })}
                />
                <CardSection
                    icon={<GetIcon name="timer-cash" size="lg" />}
                    title={intl.formatMessage({
                        id: 'gift_cards.landing_page.you_give_card_section.use_anytime.title',
                        defaultMessage: 'Use Anytime',
                    })}
                    description={intl.formatMessage({
                        id: 'gift_cards.landing_page.you_give_card_section.use_anytime.description',
                        defaultMessage:
                            "No rush—our gift cards never expire, so you can use them whenever you're ready.",
                    })}
                />
                <FeatureCardSection onCheckBalanceClick={onCheckBalanceClick} />
            </Box>
        </Flex>
    );
};

const CardSection = ({
    icon,
    title,
    description,
}: {
    icon: React.ReactNode;
    title: string;
    description: string;
}): JSX.Element => (
    <Flex direction="column" alignItems="center" gap={4}>
        <Center h="80px" w="80px" borderRadius="full" bg="#FCFBF8">
            {icon}
        </Center>
        <Header as="h4" variant="header4" align="center" textAlign="center" style="bold">
            {title}
        </Header>
        <Text as="p" type="body" size="base" align="center">
            {description}
        </Text>
    </Flex>
);

type FeatureCardSectionProps = {
    onCheckBalanceClick: () => void;
};

const FeatureCardSection = ({ onCheckBalanceClick }: FeatureCardSectionProps): JSX.Element => (
    <Flex
        direction="column"
        alignItems="center"
        gap={6}
        borderRadius="2xl"
        bg="#FCFBF8"
        p={4}
        justifyContent={{ md: 'center' }}
    >
        <Flex direction="column" alignItems="center" gap={2}>
            <GetIcon name="wallet" size="lg" />
            <Header as="h4" variant="header4" align="center" textAlign="center" style="bold">
                <FormattedMessage
                    id="gift_cards.landing_page.you_give_card_section.feature_card_section.title"
                    defaultMessage=" Gift Card Balance"
                />
            </Header>
        </Flex>
        <Button variant="tertiary" onClick={onCheckBalanceClick} id="check-balance-button">
            <FormattedMessage
                id="gift_cards.landing_page.you_give_card_section.feature_card_section.button"
                defaultMessage="Check Balance"
            />
        </Button>
    </Flex>
);
